import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { Menu, LayoutDashboard, Cloud, Settings as SettingsIcon, FileBarChart } from "lucide-react";
import { Sidebar, type NavGroup } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { useAuthContext } from "@/context/AuthContext";
import { logout } from "@/lib/auth";

const COLLAPSED_STORAGE_KEY = "sidebar_collapsed";

const CLIENT_NAV_GROUPS: NavGroup[] = [
  {
    label: "Main",
    items: [
      { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { label: "Climate Risk", href: "/assessment", icon: Cloud },
      { label: "Settings", href: "/settings", icon: SettingsIcon },
    ],
  },
  {
    label: "Assessment",
    items: [
      { label: "Reports", href: "/assessment/disclosure", icon: FileBarChart },
    ],
  },
];

export function Shell({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  const { user, workspace } = useAuthContext();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true"
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  async function handleLogout() {
    try {
      await logout();
    } finally {
      setLocation("/login");
    }
  }

  const displayName = `${user.firstName} ${user.lastName}`;
  const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  const workspaceLabel = workspace?.name ?? "";
  const sidebarWidth = collapsed ? "md:pl-16" : "md:pl-60";

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        groups={CLIENT_NAV_GROUPS}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className="sticky top-0 z-30 flex h-14 items-center border-b border-border bg-background px-4 md:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded-lg p-2 text-muted-foreground hover:bg-accent"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <div className={`transition-[padding] duration-200 ${sidebarWidth}`}>
        <TopBar
          companyName={workspaceLabel || "—"}
          displayName={displayName}
          initials={initials}
          onLogout={handleLogout}
        />
        <main className="px-6 py-6 md:px-8 md:py-8">{children}</main>
      </div>
    </div>
  );
}
